/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Table,
  Tooltip,
  Button,
  Popconfirm,
  Space,
} from 'antd';
import {
  PageSection,
  messageError,
  messageSuccess,
  ComponentLoader,
} from '@deltaohm/ant-components';

import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import { DeleteOutlined, RedoOutlined } from '@ant-design/icons';

import { extractGraphQLErrors } from '../../utils/graphql';

import { ErrorList } from '../../generics';
import {
  formatLocale,
  getLocale,
} from '../../utils/date';
import {
  WORKSPACE_QUERY,
  WORKSPACE_RETRY_CONVERT_MUTATION,
  WORKSPACE_DELETE_CONVERT_MUTATION,
  WORKSPACE_SOUND_SESSIONS_JOBS_QUERY,
  WORKSPACE_UPLOAD_FILE_MUTATION,
} from '../queries';
import WorkspaceFileForm from '../components/WorkspaceFileForm';

const WorkspaceFileTab = (props) => {
  const {
    workspace,
  } = props;

  const { t, i18n } = useTranslation();

  const locale = getLocale(i18n.language);

  const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
  const [bulkLoading, setBulkLoading] = React.useState(false);

  const {
    data: jobData,
    error: jobError,
    loading: jobLoading,
    refetch: refetchJobs,
  } = useQuery(WORKSPACE_SOUND_SESSIONS_JOBS_QUERY, {
    variables: {
      workspaceId: workspace.id,
    },
    pollInterval: 20000,
    fetchPolicy: 'network-only',
  });

  const [
    workspaceUploadFileMutationAction,
    {
      loading: uploadLoading,
      error: uploadError,
    },
  ] = useMutation(WORKSPACE_UPLOAD_FILE_MUTATION, {
    refetchQueries: () => {
      const result = [
        {
          query: WORKSPACE_QUERY,
          variables: {
            id: workspace.id,
          },
        },
      ];
      return result;
    },
    awaitRefetchQueries: true,
  });

  const [
    workspaceRetryConvertJobAction,
    {
      error: retryJobError,
    },
  ] = useMutation(WORKSPACE_RETRY_CONVERT_MUTATION);

  const [
    workspaceDeleteConvertJobAction,
    {
      error: deleteJobError,
    },
  ] = useMutation(WORKSPACE_DELETE_CONVERT_MUTATION);

  const jobQueryResult = React.useMemo(() => {
    if (!jobLoading && !jobError) {
      const { viewer } = jobData;
      const { workspace: workspaceQuery } = viewer;
      const { soundSessionsConvertJobs } = workspaceQuery;
      return {
        soundSessionsConvertJobs,
      };
    }
    return {
      soundSessionsConvertJobs: [],
    };
  }, [jobData, jobError, jobLoading]);

  const failedJobs = React.useMemo(
    () => jobQueryResult.soundSessionsConvertJobs.filter((j) => j.kind === 'FAILED'),
    [jobQueryResult.soundSessionsConvertJobs],
  );

  const handleFormSubmit = React.useCallback(async (data) => {
    try {
      await workspaceUploadFileMutationAction({
        variables: {
          input: {
            id: workspace.id,
            ...data,
          },
        },
      });
      messageSuccess(t('workspaces.fileTab.uploadSuccess'));
    }
    // eslint-disable-next-line no-empty
    catch (e) { }
  }, [workspaceUploadFileMutationAction]);

  const handleRetryJob = React.useCallback(async (id) => {
    try {
      await workspaceRetryConvertJobAction({
        variables: {
          input: {
            workspaceId: workspace.id,
            id,
          },
        },
      });
      await refetchJobs();
    }
    // eslint-disable-next-line no-empty
    catch (e) { }
  }, [workspaceRetryConvertJobAction, refetchJobs, workspace.id]);

  const handleDeleteJob = React.useCallback(async (id) => {
    try {
      await workspaceDeleteConvertJobAction({
        variables: {
          input: {
            workspaceId: workspace.id,
            id,
          },
        },
      });
      await refetchJobs();
    }
    // eslint-disable-next-line no-empty
    catch (e) { }
  }, [workspaceDeleteConvertJobAction, refetchJobs, workspace.id]);

  const handleBulkRetry = React.useCallback(async (ids) => {
    setBulkLoading(true);
    try {
      await Promise.all(
        ids.map((id) => workspaceRetryConvertJobAction({
          variables: {
            input: {
              workspaceId: workspace.id,
              id,
            },
          },
        })),
      );
      await refetchJobs();
      setSelectedRowKeys([]);
    }
    catch (e) {
      const errors = extractGraphQLErrors(e, t);
      messageError({ content: <ErrorList errors={errors} /> });
    }
    finally {
      setBulkLoading(false);
    }
  }, [workspaceRetryConvertJobAction, refetchJobs, workspace.id, t]);

  const handleBulkDelete = React.useCallback(async (ids) => {
    setBulkLoading(true);
    try {
      await Promise.all(
        ids.map((id) => workspaceDeleteConvertJobAction({
          variables: {
            input: {
              workspaceId: workspace.id,
              id,
            },
          },
        })),
      );
      await refetchJobs();
      setSelectedRowKeys([]);
    }
    catch (e) {
      const errors = extractGraphQLErrors(e, t);
      messageError({ content: <ErrorList errors={errors} /> });
    }
    finally {
      setBulkLoading(false);
    }
  }, [workspaceDeleteConvertJobAction, refetchJobs, workspace.id, t]);

  React.useEffect(() => {
    if (jobError || uploadError || retryJobError || deleteJobError) {
      const errors = extractGraphQLErrors(
        jobError
        || uploadError
        || retryJobError
        || deleteJobError,
        t,
      );
      messageError({
        content: <ErrorList errors={errors || uploadError} />,
      });
    }
  }, [jobError, uploadError, retryJobError, deleteJobError, t]);

  const selectedFailedIds = React.useMemo(
    () => selectedRowKeys.filter((id) => failedJobs.some((j) => j.id === id)),
    [selectedRowKeys, failedJobs],
  );

  const selectedDeletableIds = React.useMemo(() => {
    const deletable = new Set(
      jobQueryResult.soundSessionsConvertJobs
        .filter((j) => j.kind === 'FAILED' || j.kind === 'QUEUED')
        .map((j) => j.id),
    );
    return selectedRowKeys.filter((id) => deletable.has(id));
  }, [selectedRowKeys, jobQueryResult.soundSessionsConvertJobs]);

  const jobColumns = React.useMemo(() => ([
    {
      title: t('workspaces.fileTab.table.date'),
      dataIndex: 'date',
      render: (date) => (date ? formatLocale(new Date(date), 'PPpp', locale) : ''),
    },
    {
      title: t('workspaces.fileTab.table.filename'),
      dataIndex: 'originalFilename',
    },
    {
      title: t('workspaces.fileTab.table.jobStatus'),
      dataIndex: 'kind',
      render: (kind, record) => {
        if (record.message) {
          return (
            <Tooltip title={record.message}>
              {kind}
            </Tooltip>
          );
        }
        return kind;
      },
    },
    {
      title: t('common.actions'),
      dataIndex: 'id',
      fixed: 'right',
      render: (id, record) => {
        const result = [];
        if (record.kind === 'FAILED') {
          result.push((
            <Tooltip
              key={`${id}-retry`}
              title={t('common.retry')}
            >
              <Button
                shape="circle"
                size="small"
                onClick={() => handleRetryJob(id)}
              >
                <RedoOutlined />
              </Button>
            </Tooltip>
          ));
        }
        if (record.kind === 'FAILED' || record.kind === 'QUEUED') {
          result.push((
            <Popconfirm
              key={`${id}-delete`}
              title={t('common.sureToDelete')}
              okText={t('common.yes')}
              cancelText={t('common.no')}
              onConfirm={() => handleDeleteJob(id)}
            >
              <Tooltip title={t('common.delete')}>
                <Button shape="circle" size="small" type="ghost">
                  <DeleteOutlined />
                </Button>
              </Tooltip>
            </Popconfirm>
          ));
        }

        if (result.length) {
          return (
            <Space size="small">
              {result}
            </Space>
          );
        }

        return null;
      },
    },
  ]), [workspace, t, handleRetryJob, handleDeleteJob]);

  const rowSelection = React.useMemo(() => ({
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: record.kind !== 'FAILED' && record.kind !== 'QUEUED',
    }),
  }), [selectedRowKeys]);

  const showBulkActions = failedJobs.length > 0 || selectedRowKeys.length > 0;

  return (
    <>
      <PageSection
        title={t('workspaces.fileTab.formTitle')}
      >
        <Row>
          <Col xs={24}>
            <ComponentLoader
              loading={uploadLoading}
            >
              <WorkspaceFileForm
                onFormSubmit={handleFormSubmit}
              />
            </ComponentLoader>
          </Col>
        </Row>
      </PageSection>
      <PageSection
        title={t('workspaces.fileTab.jobTitle')}
        subtitle={t('workspaces.fileTab.jobSubtitle')}
      >
        <Row>
          <Col xs={24}>
            {showBulkActions && (
              <Space style={{ marginBottom: 8 }}>
                {failedJobs.length > 0 && (
                  <>
                    <Button
                      size="small"
                      icon={<RedoOutlined />}
                      loading={bulkLoading}
                      onClick={() => handleBulkRetry(failedJobs.map((j) => j.id))}
                    >
                      {t('workspaces.fileTab.retryAllFailed', { count: failedJobs.length })}
                    </Button>
                    <Popconfirm
                      title={t('common.sureToDelete')}
                      okText={t('common.yes')}
                      cancelText={t('common.no')}
                      onConfirm={() => handleBulkDelete(failedJobs.map((j) => j.id))}
                    >
                      <Button
                        size="small"
                        icon={<DeleteOutlined />}
                        loading={bulkLoading}
                        danger
                      >
                        {t('workspaces.fileTab.deleteAllFailed', { count: failedJobs.length })}
                      </Button>
                    </Popconfirm>
                  </>
                )}
                {selectedRowKeys.length > 0 && (
                  <>
                    {selectedFailedIds.length > 0 && (
                      <Button
                        size="small"
                        icon={<RedoOutlined />}
                        loading={bulkLoading}
                        onClick={() => handleBulkRetry(selectedFailedIds)}
                      >
                        {t('workspaces.fileTab.retrySelected', { count: selectedFailedIds.length })}
                      </Button>
                    )}
                    {selectedDeletableIds.length > 0 && (
                      <Popconfirm
                        title={t('common.sureToDelete')}
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                        onConfirm={() => handleBulkDelete(selectedDeletableIds)}
                      >
                        <Button
                          size="small"
                          icon={<DeleteOutlined />}
                          loading={bulkLoading}
                          danger
                        >
                          {t('workspaces.fileTab.deleteSelected', { count: selectedDeletableIds.length })}
                        </Button>
                      </Popconfirm>
                    )}
                  </>
                )}
              </Space>
            )}
            <Table
              bordered
              size="small"
              columns={jobColumns}
              rowKey={(record) => record.id}
              dataSource={jobQueryResult.soundSessionsConvertJobs}
              loading={jobLoading || bulkLoading}
              pagination={false}
              rowSelection={rowSelection}
            />
          </Col>
        </Row>
      </PageSection>
    </>
  );
};

const propTypes = {
  workspace: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
};

WorkspaceFileTab.propTypes = propTypes;

export default WorkspaceFileTab;
